import { createSelector } from 'reselect';

import { changeUtils } from '../../../utils';
import { deliveryKey } from '../../../utils/makeUtils';
import { getRouteProps as selectRouteProps } from '../../../weborder/redux/basket/selectors';
import {
  GetCollectionProps,
  GetDeliveryProps,
  GetRestaurantProps,
} from './restaurant.types';

export const selectRestaurantDetailsWrapper = (state: WebOrder.State) => {
  const props = selectRouteProps(state);

  if (!props) {
    return undefined;
  }

  return selectRestaurantDetails(state, props);
};

export const selectRestaurantDetails = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? selectCollectionDetails(state, props)
    : selectDeliveryDetails(state, props);

const selectCollectionDetails = createSelector(
  [
    (_state: WebOrder.State, props: GetCollectionProps) =>
      changeUtils.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetCollectionProps) =>
      changeUtils.stringToNumber(props.prId),
    (state: WebOrder.State) => state.collectionRestaurants,
  ],
  (vrId, prId, collectionDetails) => {
    if (!vrId || !prId) {
      return null;
    }

    const details = collectionDetails[prId];

    if (details && details.status === 'success') {
      return details.data;
    }

    return null;
  },
);

const selectDeliveryDetails = createSelector(
  [
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      changeUtils.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      changeUtils.stringToNumber(props.deliveryLocationId),
    (state: WebOrder.State) => state.deliveryRestaurants,
  ],
  (vrId, deliveryLocationId, deliveryRestaurants) => {
    if (!vrId || !deliveryLocationId) {
      return null;
    }

    const key = deliveryKey(vrId, deliveryLocationId);
    const details = deliveryRestaurants[key];
    if (details && details.status === 'success') {
      return details.data;
    }

    return null;
  },
);

export const selectRestaurantSummary = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? selectCollectionSummary(state, props)
    : selectDeliverySummary(state, props);

export const selectCollectionSummary = createSelector(
  [
    (_state: WebOrder.State, props: GetCollectionProps) =>
      changeUtils.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetCollectionProps) =>
      changeUtils.stringToNumber(props.prId),
    (state: WebOrder.State) => state.flipdish.CollectionSummaries,
  ],
  (vrId, prId, CollectionSummaries) => {
    if (!vrId || !prId) {
      return null;
    }

    if (CollectionSummaries && CollectionSummaries.status === 'success') {
      const config = CollectionSummaries.data.find(
        (s) =>
          s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
      );

      return config;
    }

    return null;
  },
);

export const selectDeliverySummary = createSelector(
  [
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      changeUtils.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      changeUtils.stringToNumber(props.deliveryLocationId),
    (state: WebOrder.State) => state.deliveryOptions,
  ],
  (vrId, deliveryLocationId, deliveryOptions) => {
    if (!vrId || !deliveryLocationId) {
      return null;
    }

    const deliveryOption = deliveryOptions[deliveryLocationId];
    if (deliveryOption && deliveryOption.status === 'success') {
      if (deliveryOption.data.type === 'skip') {
        return deliveryOption.data.to;
      }

      return deliveryOption.data.of.find((o) => o.VirtualRestaurantId === vrId);
    }

    return null;
  },
);
